/**
 * TariffRequestCard — запрос кафе на персональный тариф (конструктор, 00458).
 *
 * Показывается в карточке кафе (CafeDetailModal → Подписка), когда у тенанта
 * есть pending-запрос: что просят (зал/сотрудники/модули), цена сейчас → новая,
 * комментарий. «Применить» создаёт план custom-<tenant> и переводит подписку
 * (super_admin_apply_tariff_request); «Отклонить» — с причиной, кафе увидит её
 * в кабинете.
 */
import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import {
  superAdminApplyTariffRequest,
  superAdminListTariffRequests,
  superAdminRejectTariffRequest,
  type TariffRequestAdminRow,
} from "@cafe-os/db";
import { C, FONT } from "../styles/theme";
import { fmtMoney } from "../lib/money";

const HALL = ["без зала", "до 10 столов", "до 30 столов", "до 50 столов", "безлимит столов"];
const STAFF = ["до 5", "до 10", "до 20", "безлимит"];

export function describeTariffConfig(cfg: TariffRequestAdminRow["config"] | null | undefined): string {
  if (!cfg) return "";
  const mods = [cfg.delivery ? "доставка" : "", cfg.ai ? "AI" : "", cfg.wl ? "свой бренд" : ""].filter(Boolean);
  return `Зал ${HALL[cfg.hall] ?? "?"} · сотрудники ${STAFF[cfg.staff] ?? "?"} · модули: ${mods.length ? mods.join(", ") : "нет"}`;
}

/** Карточка кафе: pending-запрос этого тенанта (если есть). */
export function TariffRequestCard({ tenantId, onApplied }: { tenantId: string; onApplied?: () => void }) {
  const [req, setReq] = useState<TariffRequestAdminRow | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    superAdminListTariffRequests("pending", 200)
      .then((list) => { if (alive) setReq(list.find((r) => r.tenant_id === tenantId) ?? null); })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [tenantId]);

  if (err) return <div style={{ fontSize: 12, color: C.red, marginBottom: 12 }}>Запросы тарифа: {err}</div>;
  if (!req) return null;
  return <TariffRequestView req={req} onDone={() => { setReq(null); onApplied?.(); }} />;
}

/** Настройки → Планы: все pending-запросы платформы. */
export function TariffRequestsPanel() {
  const [list, setList] = useState<TariffRequestAdminRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    try {
      setList(await superAdminListTariffRequests("pending", 100));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }
  useEffect(() => { void load(); }, []);

  if (err) return <div style={{ fontSize: 12, color: C.red }}>Запросы тарифа: {err}</div>;
  if (list.length === 0) {
    return <div style={{ fontSize: 12, color: C.muted }}>Новых запросов на свой тариф нет.</div>;
  }
  return (
    <div style={{ display: "grid", gap: 10 }}>
      {list.map((r) => <TariffRequestView key={r.id} req={r} showTenant onDone={() => void load()} />)}
    </div>
  );
}

/** Сам запрос: что просят, цена, «Применить»/«Отклонить». */
export function TariffRequestView({ req, showTenant, onDone }: {
  req: TariffRequestAdminRow; showTenant?: boolean; onDone?: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function apply() {
    if (busy) return;
    if (!window.confirm(`Применить персональный тариф ${fmtMoney(req.monthly_price, req.currency)}/мес для ${req.tenant_id}?\n\n${describeTariffConfig(req.config)}\n\nПодписка переводится на план custom-${req.tenant_id} сразу.`)) return;
    setBusy(true);
    try {
      await superAdminApplyTariffRequest(req.id);
      onDone?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function reject() {
    if (busy) return;
    const note = window.prompt("Причина отказа (кафе увидит её в кабинете):", "");
    if (note === null) return;
    setBusy(true);
    try {
      await superAdminRejectTariffRequest(req.id, note.trim() || undefined);
      onDone?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const diff = req.monthly_price - (req.current_price ?? 0);

  return (
    <div style={{
      background: "rgba(212,166,86,0.08)", border: `1px solid ${C.brand}`,
      borderRadius: 12, padding: 16, marginBottom: 12, display: "grid", gap: 10,
    }}>
      <div style={{ fontSize: 11, color: C.brand, letterSpacing: 1, textTransform: "uppercase", fontWeight: 700 }}>
        🧩 Запрос на свой тариф · {new Date(req.created_at).toLocaleString("ru-RU")}
      </div>
      {showTenant && (
        <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>
          {req.tenant_name?.ru ?? req.tenant_name?.en ?? req.tenant_id} <code style={{ fontFamily: FONT.mono, fontSize: 11, color: C.muted }}>{req.tenant_id}</code>
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: C.red }}>{err}</div>}
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontFamily: FONT.display, fontSize: 22, fontWeight: 700, color: C.text }}>
          {fmtMoney(req.monthly_price, req.currency)}/мес
        </span>
        {req.current_price != null && (
          <span style={{ fontSize: 12, color: C.muted }}>
            сейчас {fmtMoney(req.current_price, req.currency)} → {diff >= 0 ? "+" : "−"}{fmtMoney(Math.abs(diff), req.currency)}
          </span>
        )}
      </div>
      <div style={{ fontSize: 13, color: C.text }}>{describeTariffConfig(req.config)}</div>
      {req.note && <div style={{ fontSize: 12, color: C.muted }}>💬 {req.note}</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={apply}
          disabled={busy}
          style={{
            background: C.brand, color: "#0A0E14", border: "none", borderRadius: 8,
            padding: "9px 16px", fontSize: 12, fontWeight: 800, cursor: busy ? "wait" : "pointer",
            display: "flex", alignItems: "center", gap: 6, fontFamily: FONT.body,
          }}
        ><Check size={14} /> Применить</button>
        <button
          type="button"
          onClick={reject}
          disabled={busy}
          style={{
            background: "transparent", color: C.red, border: `1px solid ${C.redSoft}`, borderRadius: 8,
            padding: "9px 14px", fontSize: 12, fontWeight: 700, cursor: busy ? "wait" : "pointer",
            display: "flex", alignItems: "center", gap: 6, fontFamily: FONT.body,
          }}
        ><X size={14} /> Отклонить</button>
      </div>
    </div>
  );
}
